/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { USER_NAV_ITEMS } from './user-nav.items';
import type { UserNavItem } from './user-nav.types';

export type { UserNavItem } from './user-nav.types';
export { USER_NAV_ITEMS } from './user-nav.items';

/**
 * Whether a user nav item is enabled — invokes its `isEnabled` predicate,
 * defaulting to enabled when none is provided.
 */
const isItemEnabled = (i: UserNavItem): boolean => i.isEnabled?.() ?? true;
/**
 * Comparator that orders user nav items by ascending desktop order.
 */
const byDesktopOrder = (a: UserNavItem, b: UserNavItem) => a.desktopOrder - b.desktopOrder;
/**
 * Comparator that orders user nav items by ascending mobile order.
 */
const byMobileOrder = (a: UserNavItem, b: UserNavItem) => a.mobileOrder - b.mobileOrder;

/**
 * Desktop sidebar items sorted by desktop order (only enabled items).
 */
export function getUserDesktopItems(): UserNavItem[] {
  return USER_NAV_ITEMS.filter((i) => isItemEnabled(i) && i.desktopPlacement === 'nav').sort(byDesktopOrder);
}

/**
 * Enabled mobile tab items sorted by ascending mobile order.
 */
const enabledMobileTabs = (): UserNavItem[] =>
  USER_NAV_ITEMS.filter((i) => isItemEnabled(i) && i.mobilePlacement === 'tab').sort(byMobileOrder);

/**
 * Promotes the second enabled tab to center when the configured center item is
 * unavailable, returning the promoted center and the remaining tabs.
 *
 * @param tabs - Enabled mobile tab items sorted by mobile order.
 * @returns {{ center: UserNavItem | null; tabs: UserNavItem[] }} Fallback layout.
 */
function promoteFallbackCenter(tabs: UserNavItem[]): { center: UserNavItem | null; tabs: UserNavItem[] } {
  if (tabs.length < 2) return { center: null, tabs };
  const promoted = tabs[1];
  return { center: promoted, tabs: tabs.filter((i) => i.id !== promoted.id) };
}

/**
 * Resolves the mobile center item and the remaining tabs. When the configured
 * center item (WhatsApp) is disabled, the second enabled tab by mobileOrder
 * is promoted to center.
 *
 * @returns {{ center: UserNavItem | null; tabs: UserNavItem[] }} Mobile layout.
 */
function resolveMobileLayout(): { center: UserNavItem | null; tabs: UserNavItem[] } {
  const configuredCenter = USER_NAV_ITEMS.find((i) => i.mobilePlacement === 'center');
  if (configuredCenter && isItemEnabled(configuredCenter)) {
    return { center: configuredCenter, tabs: enabledMobileTabs() };
  }
  return promoteFallbackCenter(enabledMobileTabs());
}

/**
 * Mobile tab items (excluding the center action), sorted by mobile order.
 */
export function getUserMobileTabs(): UserNavItem[] {
  return resolveMobileLayout().tabs;
}

/**
 * The single mobile center (highlighted) item.
 */
export function getUserMobileCenter(): UserNavItem | null {
  return resolveMobileLayout().center;
}

/**
 * Development-only invariant guard: fails fast on duplicate ids/segments.
 */
export function assertUserNavInvariants(): void {
  const ids = USER_NAV_ITEMS.map((i) => i.id);
  const segments = USER_NAV_ITEMS.map((i) => i.segment);
  const dupId = ids.find((id, idx) => ids.indexOf(id) !== idx);
  if (dupId) throw new Error(`[user-nav] duplicate id: ${dupId}`);
  const dupSeg = segments.find((s, idx) => segments.indexOf(s) !== idx);
  if (dupSeg) throw new Error(`[user-nav] duplicate segment: ${dupSeg}`);
}

if (import.meta.env?.DEV) {
  assertUserNavInvariants();
}
